import React, { PropsWithChildren } from 'react';
import { makeStyles } from '@material-ui/core';
import { UserSettingsSignInAvatar } from '@backstage/plugin-user-settings';

import {
  Sidebar,
  sidebarConfig,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarPage,
  SidebarSpace,
  useSidebarOpenState,
  Link,
} from '@backstage/core-components';
import { SidebarSearchModal } from '@backstage/plugin-search';
import { useIdentity } from '@internal/shared';

import HomeIcon from '@material-ui/icons/Home';
import LibraryBooks from '@material-ui/icons/LibraryBooks';
import MenuIcon from '@material-ui/icons/Menu';
import SettingsIcon from '@material-ui/icons/Settings';
import ContactSupportIcon from '@material-ui/icons/ContactSupport';
import BookIcon from '@material-ui/icons/Book';
import CardTravel from '@material-ui/icons/CardTravel';
import CreateComponentIcon from '@material-ui/icons/AddCircleOutline';
import ExtensionIcon from '@material-ui/icons/Extension';
import SettingsSystemDaydreamSharp from '@material-ui/icons/SettingsSystemDaydreamSharp';
import SearchIcon from '@material-ui/icons/Search';
import ThumbUpIcon from '@material-ui/icons/ThumbUp';
import UsersIcon from '@material-ui/icons/Group';
import SupervisedUserCircleIcon from '@material-ui/icons/SupervisedUserCircle';
import { QuestionMarkIcon } from '../icons/QuestionMarkIcon';
import { ApiIcon } from '../icons/ApiIcon';
import { MonitoringIcon } from '../icons/MonitoringIcon';

// Local custom icons
import LogoFull from './LogoFull';
import LogoIcon from './LogoIcon';
import {
  dxPortalAdvancedFeaturesPermission,
  dxPortalBasicFeaturesPermission,
} from '@internal/shared/src/permission/opa_permissions';
import { PermissionWrapper } from './PermissionWrapper';
import { useHasAnyUserRequestPermission } from '../userRequests/hooks';
import { OpenFeedbackModal } from '@parsifal-m/backstage-plugin-open-feedback';
import { ConfigApi, configApiRef, useApi } from '@backstage/core-plugin-api';
import { DiamSidebarGroup } from '../Diam/DiamSidebarGroup';

const useSidebarStyles = makeStyles({
  root: {
    width: sidebarConfig.drawerWidthClosed,
    height: 3 * sidebarConfig.logoHeight,
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    marginBottom: -14,
  },
  link: {
    width: sidebarConfig.drawerWidthClosed,
    marginLeft: 24,
  },
  divider: {
    backgroundColor: 'rgba(255,255,255, 0.2)',
  },
  settings: {
    marginBottom: 10,
  },
});


const UserRequestsGuard: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { hasPermission, isLoading } = useHasAnyUserRequestPermission();
  if (isLoading || !hasPermission) return null;
  return <>{children}</>;
};

const SidebarLogo = () => {
  const classes = useSidebarStyles();
  const { isOpen } = useSidebarOpenState();

  return (
    <div className={classes.root}>
      <Link to="/" underline="none" className={classes.link} aria-label="Home">
        {isOpen ? <LogoFull /> : <LogoIcon />}
      </Link>
    </div>
  );
};

type RootWithLandingPageViewProps = PropsWithChildren<{
  onOpenLogin?: () => void;
}>;

export const RootWithLandingPageView = ({
  children,
  onOpenLogin,
}: RootWithLandingPageViewProps) => {
  const classes = useSidebarStyles();
  const { idToken } = useIdentity();
  const isUserLoggedIn = Boolean(idToken);
  const openLoginDialog = onOpenLogin ?? (() => {});
  const configApi: ConfigApi = useApi(configApiRef);
  const dxmGrafanaUrl = configApi.getOptionalString('dxmGrafana.url') || '/404';
  const useDocsV2 = Boolean(
    configApi.getOptionalBoolean('featureFlags.docsV2'),
  );
  // TODO: remove this once we have a proper docs v2 page
  const docsPageRoute = useDocsV2 ? 'docs-v2' : 'docs';
  const faqPageRoute = useDocsV2
    ? '/docs-v2/help/access-and-security'
    : '/docs/default/component/dx-documentation-public/help/';

  return (
    <SidebarPage>
      <Sidebar>
        <SidebarLogo />
        {!isUserLoggedIn ? (
          <>
            <SidebarGroup label="Menu" icon={<MenuIcon />}>
              <SidebarDivider className={classes.divider} />
              <SidebarItem icon={HomeIcon} to="/" text="Home" />
              <SidebarItem
                icon={ApiIcon}
                text="APIs"
                disableHighlight
                onClick={openLoginDialog}
              />
              <SidebarItem
                icon={LibraryBooks}
                text="Docs"
                disableHighlight
                onClick={openLoginDialog}
              />
            </SidebarGroup>
            <SidebarSpace />
            <SidebarDivider className={classes.divider} />
            <SidebarGroup
              label="Support"
              icon={<UserSettingsSignInAvatar />}
              to="/contact-us"
            >
              <SidebarItem
                icon={ContactSupportIcon}
                to="/contact-us"
                text="Contact Us"
              />
            </SidebarGroup>
          </>
        ) : (
          <>
            <SidebarGroup label="Menu" icon={<MenuIcon />}>
              <SidebarDivider className={classes.divider} />
              <SidebarItem icon={HomeIcon} to="/" text="Home" />

              <DiamSidebarGroup />

              <SidebarItem icon={ApiIcon} to="api-docs" text="APIs" />
              <SidebarItem icon={LibraryBooks} to={docsPageRoute} text="Docs" />

              <SidebarItem
                icon={SettingsSystemDaydreamSharp}
                to="system-status"
                text="System Status"
              />

              <PermissionWrapper
                permission={dxPortalAdvancedFeaturesPermission}
              >
                <SidebarItem icon={CardTravel} to="toolbox" text="ToolBox" />
              </PermissionWrapper>

              <PermissionWrapper
                permission={dxPortalAdvancedFeaturesPermission}
              >
                <SidebarItem
                  icon={CreateComponentIcon}
                  to="create"
                  text="Intake Forms"
                />
              </PermissionWrapper>

              <PermissionWrapper
                permission={dxPortalAdvancedFeaturesPermission}
              >
                <SidebarItem icon={BookIcon} to="storybook" text="Storybook" />
              </PermissionWrapper>

              <PermissionWrapper
                permission={dxPortalAdvancedFeaturesPermission}
              >
                <SidebarItem
                  icon={MonitoringIcon}
                  to={dxmGrafanaUrl}
                  text="Monitoring"
                  target="_blank"
                />
              </PermissionWrapper>

              <SidebarItem
                icon={QuestionMarkIcon}
                to={faqPageRoute}
                text="FAQ"
              />

              <PermissionWrapper
                permission={dxPortalAdvancedFeaturesPermission}
              >
                <SidebarItem
                  icon={ThumbUpIcon}
                  to="open-feedback"
                  text="Reviews"
                />
              </PermissionWrapper>

              <SidebarSpace />

              <SidebarGroup>
                <SidebarDivider className={classes.divider} />
                <PermissionWrapper
                  permission={dxPortalAdvancedFeaturesPermission}
                >
                  <UserRequestsGuard>
                    <SidebarItem
                      icon={SupervisedUserCircleIcon}
                      to="user-requests"
                      text="User Requests"
                    />
                  </UserRequestsGuard>
                </PermissionWrapper>

                <PermissionWrapper
                  permission={dxPortalAdvancedFeaturesPermission}
                >
                  <SidebarItem
                    icon={UsersIcon}
                    to="user-management"
                    text="Users"
                  />
                </PermissionWrapper>

                <OpenFeedbackModal color="primary" />

                <SidebarItem
                  icon={ContactSupportIcon}
                  to="contact-us"
                  text="Contact Us"
                />

                <SidebarItem
                  icon={SettingsIcon}
                  to="settings"
                  className={classes.settings}
                  text="Settings"
                />
              </SidebarGroup>
            </SidebarGroup>
          </>
        )}
      </Sidebar>
      {children}
    </SidebarPage>
  );
};

/** Old Root with deprecated sidebar */
/** DO NOT add new sidebar items to this component */
export const Root = ({ children }: PropsWithChildren<{}>) => {
  const classes = useSidebarStyles();
  const configApi: ConfigApi = useApi(configApiRef);
  const useDocsV2 = Boolean(
    configApi.getOptionalBoolean('featureFlags.docsV2'),
  );
  const dxmGrafanaUrl = configApi.getOptionalString('dxmGrafana.url') || '/404';
  const docsPageRoute = useDocsV2 ? 'docs-v2' : 'docs';

  return (
    <SidebarPage>
      <Sidebar>
        <SidebarLogo />
        <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
          <SidebarSearchModal />
        </SidebarGroup>
        <SidebarDivider className={classes.divider} />
        <SidebarGroup label="Menu" icon={<MenuIcon />}>
          {/* Global nav, not org-specific */}
          <SidebarItem icon={HomeIcon} to="/" text="Home" />

          <DiamSidebarGroup />

          <PermissionWrapper permission={dxPortalAdvancedFeaturesPermission}>
            <SidebarItem icon={ExtensionIcon} to="api-docs" text="APIs" />
          </PermissionWrapper>

          <SidebarItem icon={LibraryBooks} to={docsPageRoute} text="Docs" />

          <PermissionWrapper permission={dxPortalAdvancedFeaturesPermission}>
            <SidebarItem
              icon={SettingsSystemDaydreamSharp}
              to="system-status"
              text="System Status"
            />
          </PermissionWrapper>

          <PermissionWrapper permission={dxPortalAdvancedFeaturesPermission}>
            <SidebarItem icon={CardTravel} to="toolbox" text="ToolBox" />
          </PermissionWrapper>

          <PermissionWrapper permission={dxPortalAdvancedFeaturesPermission}>
            <SidebarItem
              icon={CreateComponentIcon}
              to="create"
              text="Intake Forms"
            />
          </PermissionWrapper>

          <PermissionWrapper permission={dxPortalAdvancedFeaturesPermission}>
            <SidebarItem icon={BookIcon} to="storybook" text="Storybook" />
          </PermissionWrapper>
          <PermissionWrapper permission={dxPortalAdvancedFeaturesPermission}>
            <SidebarItem
              icon={MonitoringIcon}
              to={dxmGrafanaUrl}
              text="Monitoring"
              target="_blank"
            />
          </PermissionWrapper>

          <PermissionWrapper permission={dxPortalBasicFeaturesPermission}>
            <SidebarItem
              icon={QuestionMarkIcon}
              to="/docs/default/component/dx-documentation-public/help/"
              text="FAQ"
            />
          </PermissionWrapper>

          <PermissionWrapper permission={dxPortalAdvancedFeaturesPermission}>
            <SidebarItem icon={ThumbUpIcon} to="open-feedback" text="Reviews" />
          </PermissionWrapper>

          {/* End global nav */}
        </SidebarGroup>

        <SidebarSpace />

        <SidebarGroup>
          <SidebarDivider className={classes.divider} />
          <PermissionWrapper permission={dxPortalAdvancedFeaturesPermission}>
            <UserRequestsGuard>
              <SidebarItem
                icon={SupervisedUserCircleIcon}
                to="user-requests"
                text="User Requests"
              />
            </UserRequestsGuard>
          </PermissionWrapper>

          <PermissionWrapper permission={dxPortalAdvancedFeaturesPermission}>
            <SidebarItem icon={UsersIcon} to="user-management" text="Users" />
          </PermissionWrapper>

          <PermissionWrapper permission={dxPortalAdvancedFeaturesPermission}>
            <OpenFeedbackModal color="primary" />
          </PermissionWrapper>

          <PermissionWrapper permission={dxPortalBasicFeaturesPermission}>
            <SidebarItem
              icon={ContactSupportIcon}
              to="contact-us"
              text="Contact Us"
            />
          </PermissionWrapper>

          <SidebarItem
            icon={SettingsIcon}
            to="settings"
            className={classes.settings}
            text="Settings"
          />
        </SidebarGroup>
      </Sidebar>
      {children}
    </SidebarPage>
  );
};
